import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Button, Dialog, DialogActions, DialogContent, List, ListItem, ListItemText, Typography } from "@mui/material";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatTimeInTimezone } from "@notemeal/utils-date-time";
import { useEffect, useState } from "react";
import DialogTitle from "../../componentLibrary/DialogTitle";
import { getTeamAthleteJerseyNumberForDisplay } from "shared-ui/Athlete/utils";
import { getOrderPickupTime, getTicketOrderKey } from "./ticketReorder";
import { GenericOrderPreview } from "./Ticket/types";
import { getTicketDisplayNameForAthlete } from "./Ticket/utils";

interface ReorderTicketsDialogProps {
  open: boolean;
  orders: readonly GenericOrderPreview[];
  onClose: () => void;
  onSave: (desiredOrder: readonly GenericOrderPreview[]) => void;
}

const getOrdererName = (order: GenericOrderPreview): string => {
  if (!order.athlete) {
    return order.userFullName;
  }
  const teamId = order.athlete.team.id;
  return getTicketDisplayNameForAthlete({
    ...order.athlete,
    jerseyNumber: getTeamAthleteJerseyNumberForDisplay(teamId, order.athlete.athleteTeams, order.athlete.jerseyNumber),
  });
};

const getOrderCode = (order: GenericOrderPreview): string =>
  order.__typename === "MealMenuPlateOrder" ? order.parentBulkOrder.code : order.code;

const SortableRow = ({ order, clientTimezone }: { order: GenericOrderPreview; clientTimezone: string }) => {
  const id = getTicketOrderKey(order);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const pickupTimeForDisplay = formatTimeInTimezone(getOrderPickupTime(order), clientTimezone, { excludeTimezoneSuffix: true });

  return (
    <ListItem
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
      secondaryAction={
        <Box sx={{ display: "flex", alignItems: "center", color: "greyscale.500", pointerEvents: "none" }}>
          <DragIndicatorIcon />
        </Box>
      }
    >
      <ListItemText
        primary={getOrdererName(order)}
        secondary={
          <Typography variant="body2" color="text.secondary" component="span">
            {pickupTimeForDisplay} · #{getOrderCode(order)} · {order.itemCount} item{order.itemCount === 1 ? "" : "s"}
          </Typography>
        }
      />
    </ListItem>
  );
};

const ReorderTicketsDialog = ({ open, orders, onClose, onSave }: ReorderTicketsDialogProps) => {
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [draftOrders, setDraftOrders] = useState<GenericOrderPreview[]>([...orders]);

  // Reset draft only when the dialog opens — not when live order data refreshes.
  useEffect(() => {
    if (open) {
      setDraftOrders([...orders]);
    }
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    setDraftOrders(current => {
      const fromIndex = current.findIndex(o => getTicketOrderKey(o) === active.id);
      const toIndex = current.findIndex(o => getTicketOrderKey(o) === over.id);
      if (fromIndex < 0 || toIndex < 0) {
        return current;
      }
      return arrayMove(current, fromIndex, toIndex);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle title="Reorder Tickets" onClose={onClose} />
      <DialogContent sx={{ px: 0 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={draftOrders.map(getTicketOrderKey)} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              {draftOrders.map(order => (
                <SortableRow key={getTicketOrderKey(order)} order={order} clientTimezone={clientTimezone} />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(draftOrders);
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReorderTicketsDialog;
